import { Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import styles from './ErrorFallback.module.scss';
import { isApiError } from '@/api/REST/errors';
import { getErrorMessages } from '@/api/REST/errors';

interface ErrorFallbackProps {
  error: Error | null;
  resetErrorBoundary?: () => void;
}

/**
 * Error fallback UI component
 * Displays when ErrorBoundary catches a React rendering error
 */
export const ErrorFallback = ({
  error,
  resetErrorBoundary,
}: ErrorFallbackProps) => {
  const { t, i18n } = useTranslation(['ui', 'errors']);

  const handleRefresh = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      window.location.reload();
    }
  };

  // Extract error messages
  const errorMessages = error
    ? isApiError(error)
      ? getErrorMessages(error, { i18n, namespace: 'errors' })
      : [error.message || t('errors:common.UnexpectedError')]
    : [t('errors:common.UnexpectedError')];

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      gap={3}
      className={styles.ErrorFallback}
    >
      <Typography variant="h1" color="error">
        {t('ui:errorBoundary.Title')}
      </Typography>

      <Typography variant="body1" color="text.secondary">
        {t('ui:errorBoundary.Message')}
      </Typography>

      {errorMessages.length > 0 && (
        <Stack gap={1}>
          {errorMessages.map((msg, idx) => (
            <Typography key={idx} color="error">
              {msg}
            </Typography>
          ))}
        </Stack>
      )}

      <Button
        variant="contained"
        onClick={handleRefresh}
        data-automation-id="ErrorFallbackRefreshButton"
      >
        {t('ui:errorBoundary.RefreshButton')}
      </Button>
    </Stack>
  );
};
